<ngx-loading [show]="isLoading"></ngx-loading>
<div
  *ngIf="data?.length"
  class="mat-elevation-z8 overflow-x-auto sm:overflow-y-auto overflow-y-hidden"
>
  <table
    [dataSource]="dataSource"
    mat-table
    multiTemplateDataRows
    cdkDropList
    cdkDropListLockAxis="y"
    [cdkDropListDisabled]="isDragInert()"
    (cdkDropListDropped)="onReportDrop($event)"
  >
    <!-- Drag Handle Column (only rendered when reorderEnabled) -->
    <!-- The handle span is the cdkDrag (so it can live in a cell template) and moves its closest
         tr (cdkDragRootElement). It is also its own cdkDragHandle, which CDK registers as the only
         handle, so a drag starts from the icon alone and the rest of the row keeps native text
         selection and touch scrolling. -->
    <ng-container [matColumnDef]="DRAG_HANDLE_COLUMN">
      <th *matHeaderCellDef class="drag-handle-cell" mat-header-cell></th>
      <td
        *matCellDef="let element"
        (click)="$event.stopPropagation()"
        class="drag-handle-cell"
        mat-cell
      >
        <span
          cdkDrag
          cdkDragHandle
          cdkDragRootElement="tr"
          cdkDragLockAxis="y"
          [cdkDragData]="element"
          [cdkDragDisabled]="isDragInert()"
          [matTooltip]="dragHandleTooltip()"
          [attr.aria-label]="dragHandleTooltip() || ('Drag Report' | transloco)"
          [attr.aria-disabled]="isDragInert()"
          [class.cursor-move]="!isDragInert()"
          [class.cursor-not-allowed]="isDragInert()"
          [class.opacity-40]="isDragInert()"
          class="drag-handle inline-flex items-center justify-center"
        >
          <mat-icon class="icon-size-5" svgIcon="mat_outline:drag_indicator"></mat-icon>
          <div
            *cdkDragPreview
            class="report-drag-preview flex flex-row items-center gap-3 px-4 py-3 rounded-lg bg-accent-700"
          >
            <mat-icon class="icon-size-5" svgIcon="mat_outline:drag_indicator"></mat-icon>
            <span class="text-secondary">{{ element.short_id }}</span>
            <span class="font-medium">{{
              element.report_name | truncate: 40 : transloco.getActiveLang() | titlecase
            }}</span>
          </div>
        </span>
      </td>
    </ng-container>

    <!-- Select Column (only rendered when selectable, V2CORE-3363) -->
    <ng-container [matColumnDef]="SELECT_COLUMN">
      <th *matHeaderCellDef class="select-cell" mat-header-cell>
        <mat-checkbox
          data-testid="reports-table-select-all"
          color="primary"
          [checked]="isPageSelected()"
          [indeterminate]="isPagePartlySelected()"
          [attr.aria-label]="'Select all' | transloco"
          [matTooltip]="'Select all' | transloco"
          (click)="$event.stopPropagation()"
          (change)="onPageSelectionToggled()"
        ></mat-checkbox>
      </th>
      <td *matCellDef="let element" (click)="$event.stopPropagation()" class="select-cell" mat-cell>
        <mat-checkbox
          data-testid="reports-table-select-row"
          color="primary"
          [checked]="isRowSelected(element)"
          [attr.aria-label]="'Select report' | transloco"
          (change)="onRowSelectionToggled(element)"
        ></mat-checkbox>
      </td>
    </ng-container>

    <!-- Short Id Column -->
    <ng-container matColumnDef="short_id">
      <th *matHeaderCellDef mat-header-cell>
        {{ 'Report Id' | transloco }}
      </th>
      <td *matCellDef="let element" class="cursor-pointer" mat-cell>
        <div class="flex flex-row items-center">
          <span [matTooltip]="'Copy Report Id' | transloco" class="me-2">{{
            element.short_id
          }}</span>
          <mat-icon
            (click)="copyShortId(element.short_id, $event)"
            class="w-5 h-5 icon-size-5"
            svgIcon="mat_outline:content_copy"
          ></mat-icon>
        </div>
      </td>
    </ng-container>

    <!-- Report Name Column -->
    <ng-container matColumnDef="report_name">
      <th *matHeaderCellDef class="ps-2" mat-header-cell>
        {{ 'Report Name' | transloco }}
      </th>
      <td *matCellDef="let element" class="ps-2" mat-cell>
        {{ element.report_name | truncate: 25 : transloco.getActiveLang() | titlecase }}
        <span *ngIf="isSharedReportsMode()" class="text-xs px-2 py-0.5 rounded bg-accent-700 ms-2">
          {{ 'Shared' | transloco }}
        </span>
        <span
          *ngIf="!isSharedReportsMode() && element.failed_report_floors_count > 0"
          [matTooltip]="'Failed floors' | transloco"
        >
          <mat-icon
            class="icon-size-4 pt-1 ms-1"
            color="primary"
            svgIcon="mat_solid:warning"
          ></mat-icon>
        </span>
        <span
          *ngIf="!isSharedReportsMode() && element.is_merged"
          [matTooltip]="'Merged Report' | transloco"
        >
          <mat-icon class="icon-size-4 pt-1 ms-1" color="primary" svgIcon="call_merge"></mat-icon>
        </span>
        <div
          *ngIf="!isSharedReportsMode() && element.merged_at"
          class="text-secondary text-smaller"
        >
          {{ 'Merged at' }}:
          <span [innerHTML]="element.merged_at | dateTransform"></span>
        </div>
        <div
          *ngIf="!isSharedReportsMode() && element.sharing?.is_shared_from_external"
          class="text-secondary text-smaller"
        >
          {{ 'Shared from' | transloco }}:
          {{ element.sharing?.shared_from_organization || ('External organization' | transloco) }}
        </div>
      </td>
    </ng-container>

    <!-- Project Number Column (PC-2278) -->
    <ng-container matColumnDef="project_number">
      <th *matHeaderCellDef class="ps-2" mat-header-cell>
        {{ 'Project Number' | transloco }}
      </th>
      <td *matCellDef="let element" class="ps-2" mat-cell>
        {{ element.project_number || '-' }}
      </td>
    </ng-container>

    <!-- Camera Serial Number Column (PC-2278) -->
    <ng-container matColumnDef="camera_serial_numbers">
      <th *matHeaderCellDef class="ps-2" mat-header-cell>
        {{ 'Camera Serial Number' | transloco }}
      </th>
      <td *matCellDef="let element" class="ps-2" mat-cell>
        {{ element.camera_serial_numbers?.length ? element.camera_serial_numbers.join(', ') : '-' }}
      </td>
    </ng-container>

    <!-- Project Name Column -->
    <ng-container matColumnDef="project_name" *ngIf="globalAccess">
      <th *matHeaderCellDef mat-header-cell>
        {{ 'Project Name' | transloco }}
      </th>
      <td
        *matCellDef="let element"
        mat-cell
        [matTooltip]="element.project_name || 'N/A' | titlecase"
      >
        {{ element.project_name || 'N/A' | truncate: 25 : transloco.getActiveLang() | titlecase }}
      </td>
    </ng-container>

    <!-- Published at Column -->
    <ng-container matColumnDef="published_at">
      <th *matHeaderCellDef class="hidden sm:table-cell" mat-header-cell>
        {{ 'Published At' | transloco }}
      </th>
      <td *matCellDef="let element" class="hidden sm:table-cell text-sm px-1 truncate" mat-cell>
        <div class="flex items-center gap-2">
          <span [innerHTML]="element.published_at | dateTransform"></span>
          <div class="inline-block">
            <mat-icon
              *ngIf="globalAccess && isPublishedToday(element.published_at)"
              svgIcon="my:stop-watch"
              class="circle-due-date p-1 mt-2 cursor-default icon-size-6"
              [ngClass]="{
                'bg-primary bg-opacity-40 text-white': true
              }"
              [matTooltip]="'Published today' | transloco"
            >
            </mat-icon>
          </div>
        </div>
      </td>
    </ng-container>

    <!-- Creator Name Column -->
    <ng-container matColumnDef="creator">
      <th *matHeaderCellDef mat-header-cell>
        {{ 'Published By' | transloco }}
      </th>
      <td *matCellDef="let element" mat-cell>
        <span [matTooltip]="element.published_by | titlecase" class="published-by-cell">
          {{ element.published_by | titlecase }}
        </span>
      </td>
    </ng-container>

    <!-- Report Type Column -->
    <ng-container matColumnDef="report_type">
      <th *matHeaderCellDef mat-header-cell style="text-align-last: center">
        {{ 'Report Source' | transloco }}
      </th>
      <td
        *matCellDef="let element"
        mat-cell
        class="flex flex-row max-w-40 cursor-default items-center justify-center gap-1 rounded-full mt-3 p-1 h-6"
      >
        <span>
          <mat-icon
            class="pt-1 icon-size-4"
            color="primary"
            [svgIcon]="
              element.report_type === 'images' ? 'mat_outline:image' : 'mat_outline:videocam'
            "
          ></mat-icon>
        </span>
        <span>{{ (element.report_type === 'images' ? 'Image' : 'Video') | transloco }}</span>
      </td>
    </ng-container>

    <!--Status of the report-->
    <ng-container matColumnDef="report_status">
      <th *matHeaderCellDef class="text-center" mat-header-cell>
        {{ 'Report Status' | transloco }}
      </th>
      <td *matCellDef="let report; let index = index" mat-cell>
        <div class="flex items-center justify-center">
          <div class="flex flex-row gap-2 items-center text-center justify-center relative w-3/4">
            <!--  if the report is published-->
            <div *ngIf="report.status === REPORT_STATUS.PUBLISHED" class="rounded-lg w-full">
              {{ 'Published' | transloco }}
            </div>
            <!--  if the report is partially published-->
            <div
              *ngIf="report.status === REPORT_STATUS.PARTIALLY_PUBLISHED"
              class="rounded-lg w-full"
            >
              {{ 'Partially published ' | transloco }}
            </div>
          </div>
        </div>
      </td>
    </ng-container>

    <!-- PPE auto-detection status (V2CORE-2901) -->
    <ng-container [matColumnDef]="PPE_STATUS_COLUMN">
      <th *matHeaderCellDef class="text-center" mat-header-cell>
        {{ 'ppe.column.header' | transloco }}
      </th>
      <td *matCellDef="let report" class="text-center" mat-cell>
        <app-ppe-status-chip
          [attr.data-testid]="'report-ppe-status-' + report.report_id"
          [status]="ppeStatusOf(report)"
          [count]="ppeCountOf(report)"
          [dense]="true"
        ></app-ppe-status-chip>
      </td>
    </ng-container>

    <!-- Number of Floors Column -->
    <ng-container matColumnDef="floors">
      <th *matHeaderCellDef class="text-center" mat-header-cell>
        {{ '#Floors' | transloco }}
      </th>
      <td *matCellDef="let report" class="text-center" mat-cell>
        {{ report.report_floors.length }}
      </td>
    </ng-container>

    <ng-container matColumnDef="source_organization">
      <th *matHeaderCellDef mat-header-cell>
        {{ 'Source Organization' | transloco }}
      </th>
      <td *matCellDef="let element" mat-cell>
        {{ element.source_organization || ('Unknown organization' | transloco) }}
      </td>
    </ng-container>

    <ng-container matColumnDef="shared_by_name">
      <th *matHeaderCellDef mat-header-cell>
        {{ 'Shared By' | transloco }}
      </th>
      <td *matCellDef="let element" mat-cell>
        {{ element.shared_by_name || ('Unknown user' | transloco) }}
      </td>
    </ng-container>

    <ng-container matColumnDef="shared_at">
      <th *matHeaderCellDef mat-header-cell>
        {{ 'Shared Date' | transloco }}
      </th>
      <td *matCellDef="let element" mat-cell>
        <span [innerHTML]="element.shared_at | dateTransform"></span>
      </td>
    </ng-container>

    <ng-container matColumnDef="floors_count">
      <th *matHeaderCellDef class="text-center" mat-header-cell>
        {{ '#Floors' | transloco }}
      </th>
      <td *matCellDef="let element" class="text-center" mat-cell>
        {{ element.floors_count }}
      </td>
    </ng-container>

    <ng-container matColumnDef="points_count">
      <th *matHeaderCellDef class="text-center" mat-header-cell>
        {{ '#Points' | transloco }}
      </th>
      <td *matCellDef="let element" class="text-center" mat-cell>
        {{ element.points_count }}
      </td>
    </ng-container>

    <!-- Actions Column -->
    <ng-container matColumnDef="actions">
      <th *matHeaderCellDef mat-header-cell style="text-align-last: center"></th>
      <td *matCellDef="let element" mat-cell>
        <div *ngIf="!isViewOnly && !isSharedReportsMode()" class="flex flex-row gap-4 justify-end">
          <button
            type="button"
            *ngIf="isReorderUiEnabled()"
            (click)="onMoveRequested(element); $event.stopPropagation()"
            [disabled]="isLoading"
            [matTooltip]="'Move report' | transloco"
            [attr.aria-label]="'Move report' | transloco"
          >
            <mat-icon class="icon-size-5" svgIcon="mat_outline:swap_vert"></mat-icon>
          </button>

          <button
            type="button"
            (click)="onUnmergeReport(element.report_id)"
            *ngIf="
              element.is_merged &&
              canMergeReport &&
              !globalAccess &&
              !isReadOnlySyncedReport(element) &&
              !isUnavailableForSync(element)
            "
            [matTooltip]="'Unmerge Report' | transloco"
            [attr.aria-label]="'Unmerge Report' | transloco"
          >
            <mat-icon class="icon-size-5" svgIcon="mat_solid:call_split"></mat-icon>
          </button>

          <button
            type="button"
            *ngIf="!globalAccess"
            (click)="setSelectedReport(element); $event.stopPropagation()"
            [disabled]="!canOpenReport || isUnavailableForSync(element)"
            [matTooltip]="'View Report' | transloco"
            [attr.aria-label]="'View Report' | transloco"
          >
            <mat-icon class="icon-size-5" svgIcon="heroicons_outline:eye"></mat-icon>
          </button>

          <button
            type="button"
            *ngIf="
              !globalAccess &&
              isClonedProject &&
              !isReadOnlySyncedReport(element) &&
              !isUnavailableForSync(element)
            "
            (click)="onShareToOriginal(element.report_id); $event.stopPropagation()"
            [disabled]="!canShareToOriginal"
            [matTooltip]="'Share to Original Project' | transloco"
            [attr.aria-label]="'Share to Original Project' | transloco"
          >
            <mat-icon class="icon-size-5" svgIcon="mat_outline:share"></mat-icon>
          </button>

          <a
            *ngIf="canOpenReport && !isUnavailableForSync(element)"
            (click)="openReport(element?.report_id)"
            [matTooltip]="
              hasOpenableFloors(element) ? ('Open Report' | transloco) : ('No Floors' | transloco)
            "
            [queryParams]="hasOpenableFloors(element) ? { report_id: element?.report_id } : null"
            [routerLink]="
              hasOpenableFloors(element)
                ? ['/project/' + (element?.project_id ? element?.project_id : projectId)]
                : null
            "
            target="_blank"
          >
            <mat-icon class="icon-size-5" svgIcon="view_in_ar"></mat-icon>
          </a>

          <button
            type="button"
            *ngIf="
              !globalAccess && !isReadOnlySyncedReport(element) && !isUnavailableForSync(element)
            "
            (click)="onDeleteReport(element?.report_id)"
            [disabled]="!canDeleteReport"
            [matTooltip]="'Delete Report' | transloco"
            [attr.aria-label]="'Delete Report' | transloco"
          >
            <mat-icon class="icon-size-5 text-warn-500" svgIcon="mat_solid:delete"></mat-icon>
          </button>
        </div>

        <div *ngIf="!isViewOnly && isSharedReportsMode()" class="flex flex-row gap-2 justify-end">
          <button
            (click)="openSharedReport(element, $event)"
            type="button"
            [matTooltip]="
              hasOpenableFloors(element) ? ('View Report' | transloco) : ('No Floors' | transloco)
            "
            [attr.aria-label]="'View Report' | transloco"
          >
            <mat-icon class="icon-size-5" svgIcon="heroicons_outline:eye"></mat-icon>
          </button>
          <button
            (click)="compareSharedReport(element, $event)"
            type="button"
            [matTooltip]="
              hasOpenableFloors(element)
                ? ('Compare Report' | transloco)
                : ('No Floors' | transloco)
            "
            [attr.aria-label]="'Compare Report' | transloco"
          >
            <mat-icon class="icon-size-5" svgIcon="mat_solid:compare"></mat-icon>
          </button>
        </div>
      </td>
    </ng-container>

    <tr *matHeaderRowDef="columnsToDisplay" class="bg-accent-600" mat-header-row></tr>
    <tr
      (click)="setSelectedReport(element)"
      *matRowDef="let element; columns: columnsToDisplay"
      [ngClass]="{
        'opacity-60': !isSharedReportsMode() && isUnavailableForSync(element),
        'red-alert':
          commandScreenModeEnabled &&
          shouldAnimateRow(element) &&
          hasReportAlert(element) &&
          getReportAlertType(element) === 'warn'
      }"
      class="example-element-row cursor-pointer"
      mat-row
    ></tr>
  </table>
</div>

<mat-paginator
  [class.invisible]="!data?.length ? true : null"
  [pageSizeOptions]="[10, 20, 40, 60]"
  [length]="metaData?.total_items"
  [pageSize]="metaData?.per_page || 10"
  (page)="onPageChanged($event)"
  showFirstLastButtons
>
</mat-paginator>

<ng-container *ngIf="!isLoading && data?.length === 0">
  <app-error-template
    *ngIf="!isLoading && !data?.length"
    [bodyMessage]="
      isSharedReportsMode()
        ? ('No reports have been shared with this project yet' | transloco)
        : ('No Reports created yet' | transloco)
    "
    [type]="'empty'"
    [imageSrc]="emptyStateImageSrc"
    [imageAlt]="'NWC logo'"
  ></app-error-template>
</ng-container>
